import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    Tooltip,
    styled,
} from "@mui/material";
import {
    Add as AddIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    ArrowDownward as ArrowDownwardIcon,
    ArrowUpward as ArrowUpwardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAxiosWithAuth } from "../common/api/hooks/useAxiosWithAuth";
import { URL } from "../common/api/urls";
import { TEXT_MSGS } from "../common/utility/constant";
import ConfirmActionDialog from "./dashboard/components/confirm_dialog";
import {
    isPublishTimePast,
    parseCreatedTimeMs,
    parseTargetPublishTimeMs,
} from "../components/MarketingMessages/marketingApi";
import PopupRenderer from "./PopupRenderer";

const RootContainer = styled(Box)({
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#081329",
    color: "#ffffff",
    padding: "32px 40px 48px",
    boxSizing: "border-box",
});

const ListCard = styled(Box)({
    backgroundColor: "#0d2147",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    overflow: "hidden",
    overflowX: "auto",
});

const ROW_GRID =
    "minmax(140px, 1fr) minmax(220px, 2.4fr) minmax(100px, 0.9fr) minmax(150px, 1.1fr) minmax(150px, 1.1fr) minmax(120px, 1.2fr) minmax(72px, 0.7fr) minmax(88px, 0.8fr) 188px";

const Row = styled(Box)({
    display: "grid",
    gridTemplateColumns: ROW_GRID,
    gap: "12px 24px",
    padding: "18px 28px",
    alignItems: "center",
    minWidth: "1100px",
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
    "&:last-of-type": { borderBottom: "none" },
});

const HeaderRow = styled(Row)({
    backgroundColor: "#0a1a38",
    padding: "20px 28px",
    alignItems: "center",
});

function TableCell({ align = "start", children }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: align === "center" ? "center" : "flex-start",
                minWidth: 0,
                width: "100%",
                px: 0.5,
                textAlign: align === "center" ? "center" : "left",
                overflow: "hidden",
            }}
        >
            {children}
        </Box>
    );
}

// Reconciles whatever casing/vocabulary the backend uses ("queued", "Draft", etc.)
// into a single label+color. Backend status strings and this feature's frontend
// status vocab don't currently line up 1:1 (see chat notes on handleConfirmPublish) —
// this map is the one place that absorbs that mismatch. Add entries here, not
// scattered conditionals, when a new backend status value shows up.
const STATUS_STYLES = {
    draft: { bg: "rgba(255, 193, 7, 0.15)", color: "#ffc107", label: "Draft" },
    queued: {
        bg: "rgba(63, 105, 248, 0.2)",
        color: "#7a9cff",
        label: "Queued",
    },
    scheduled: {
        bg: "rgba(63, 105, 248, 0.2)",
        color: "#7a9cff",
        label: "Scheduled",
    },
    inprocess: {
        bg: "rgba(63, 105, 248, 0.2)",
        color: "#7a9cff",
        label: "InProcess",
    },
    delivered: {
        bg: "rgba(40, 167, 69, 0.2)",
        color: "#28a745",
        label: "Delivered",
    },
    cancelled: {
        bg: "rgba(244, 67, 54, 0.15)",
        color: "#ff6b6b",
        label: "Cancelled",
    },
    cancel: {
        bg: "rgba(244, 67, 54, 0.15)",
        color: "#ff6b6b",
        label: "Cancelled",
    },
    "in_process": {
        bg: "rgba(255, 152, 0, 0.2)",
        color: "#ff9800",
        label: "In Process",
    },
};

function StatusChip({ status, processing }) {
    let key = (status || "").toLowerCase();
    if(processing && key == "queued") key = "inprocess"
    const style = STATUS_STYLES[key] || {
        bg: "rgba(255,255,255,0.1)",
        color: "#fff",
        label: status || "—",
    };
    return (
        <Chip
            label={style.label}
            size="small"
            sx={{
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: "4px",
                height: "24px",
            }}
        />
    );
}

function formatTimeMs(ms) {
    if (ms == null) return "—";
    return new Date(ms).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    });
}

// One accessor per sortable column. Field identifiers are plain strings used
// directly as object keys — no separate "enum" object mapping strings to themselves.
const SORT_ACCESSORS = {
    title: (item) => (item.popup_data_obj?.title || "").toLowerCase(),
    status: (item) => (item.status || "").toLowerCase(),
    target_publish_time: (item) =>
        parseTargetPublishTimeMs(item.target_publish_time),
    created_time: (item) => parseCreatedTimeMs(item.created_time),
    audience: (item) =>
        (item.user_access_key_list || []).join(", ").toLowerCase(),
    type: (item) => (item.popup_data_obj?.type || "").toLowerCase(),
};

function sortMessages(messages, sortField, sortDirection) {
    const dir = sortDirection === "asc" ? 1 : -1;
    const getValue = SORT_ACCESSORS[sortField];
    return [...messages].sort((a, b) => {
        const aVal = getValue(a);
        const bVal = getValue(b);

        if (
            sortField === "target_publish_time" ||
            sortField === "created_time"
        ) {
            return ((aVal ?? -Infinity) - (bVal ?? -Infinity)) * dir;
        }
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
    });
}

function SortableHeader({
    label,
    field,
    sortField,
    sortDirection,
    onSort,
    align = "start",
}) {
    const active = sortField === field;
    return (
        <TableCell align={align}>
            <Box
                component="button"
                type="button"
                onClick={() => onSort(field)}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: align === "center" ? "center" : "flex-start",
                    gap: 0.5,
                    p: 0,
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                    color: active ? "#a8c4ff" : "#7a9cff",
                    font: "inherit",
                    textAlign: align === "center" ? "center" : "left",
                    width: align === "center" ? "100%" : "auto",
                    "&:hover": { color: "#c5d8ff" },
                }}
            >
            <Typography
                component="span"
                variant="caption"
                sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    lineHeight: 1.6,
                }}
            >
                {label}
            </Typography>
            {active &&
                (sortDirection === "asc" ? (
                    <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                ) : (
                    <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                ))}
            </Box>
        </TableCell>
    );
}

function plainTextPreview(markdown, maxLen = 90) {
    if (!markdown) return "";
    const stripped = markdown
        .replace(/[#>*_`-]/g, "")
        .replace(/\n+/g, " ")
        .trim();
    return stripped.length > maxLen
        ? `${stripped.slice(0, maxLen)}…`
        : stripped;
}

export default function MarketingMessagesListPage() {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [sortField, setSortField] = useState("created_time");
    const [sortDirection, setSortDirection] = useState("desc");
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [, listResponse, listError, listLoaded, resetList, executeListAPI] =
        useAxiosWithAuth();
    const [,,,,, executeSaveAPI] = useAxiosWithAuth();
    const [,,,,, executeDeleteAPI] = useAxiosWithAuth();
    // Preview popup state — holds the popup_data_obj of whichever row was
    // clicked, or null when no preview is open. PopupRenderer itself decides
    // what to render based on popup_data_obj.type.
    const [previewData, setPreviewData] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: null,
        item: null,
    });
    const handleDelete = (item) => {
        if ((item.status || "").toLowerCase() !== "draft") {
            return;
        }
        setConfirmDialog({
            open: true,
            type: "delete",
            item,
        });
    };

    const handleCancel = (item) => {
        const currentStatus = (item.status || "").toLowerCase();
        if (
            isPublishTimePast(item.target_publish_time) ||
            item.in_process === true ||
            currentStatus === "delivered" ||
            currentStatus === "cancel" ||
            currentStatus === "cancelled"
        ) {
            return;
        }

        setConfirmDialog({
            open: true,
            type: "cancel",
            item,
        });
    };

    const handleConfirmAction = async () => {
        const { type, item } = confirmDialog;

        if (!item) return;

        try {
            if (type === "delete") {
                await executeDeleteAPI(URL.INFO_MESSAGE_DELETE, "DELETE", {
                    id: item.id,
                });
            }

            if (type === "cancel") {
                await executeSaveAPI(URL.SAVE_INFO_MESSAGE, "POST", {
                    id: item.id,
                    status: "cancelled",
                });
            }

            setConfirmDialog({
                open: false,
                type: null,
                item: null,
            });

            fetchMessages();
        } catch (error) {
            console.error(`Failed to ${type} marketing message`, error);
        }
    };
    const fetchMessages = () => {
        executeListAPI(URL.INFO_MESSAGES_LIST, "GET");
    };

    useEffect(() => {
        document.title = "Marketing Messages";
        fetchMessages();
        const intervalId = setInterval(fetchMessages, 300_000);
        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!listLoaded) return;

        if (listResponse != null) {
            setMessages(listResponse?.result ?? []);
            setHasLoadedOnce(true);
            resetList();
            return;
        }

        if (listError != null) {
            setHasLoadedOnce(true);
            resetList();
        }
    }, [listLoaded, listResponse, listError, resetList]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection(
                field === "target_publish_time" || field === "created_time"
                    ? "desc"
                    : "asc",
            );
        }
    };

    const handlePreview = (item) => {
        // popup_data_obj may be missing/malformed on a bad row — PopupRenderer
        // already no-ops safely on a falsy/unrecognized value, so no guard needed here.
        setPreviewData(item.popup_data_obj);
    };

    const sortedMessages = useMemo(
        () => sortMessages(messages, sortField, sortDirection),
        [messages, sortField, sortDirection],
    );

    const isLoading = !hasLoadedOnce;
    const isEmpty = hasLoadedOnce && listError == null && messages.length === 0;

    return (
        <RootContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#fff" }}
                >
                    Marketing Messages
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Tooltip title="Refresh messages" arrow>
                        <IconButton
                            onClick={fetchMessages}
                            sx={{ color: "#b0c4de" }}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/marketing-messages/new")}
                        sx={{
                            backgroundColor: "#3f69f8",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { backgroundColor: "#2b52d9" },
                        }}
                    >
                        New Message
                    </Button>
                </Box>
            </Box>

            <ListCard>
                <HeaderRow>
                    <TableCell align="center">
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#7a9cff",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                lineHeight: 1.6,
                            }}
                        >
                            Message ID
                        </Typography>
                    </TableCell>
                    <SortableHeader
                        label="Title"
                        field="title"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        align="start"
                    />
                    <SortableHeader
                        label="Status"
                        field="status"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        align="center"
                    />
                    <SortableHeader
                        label="Created"
                        field="created_time"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        align="center"
                    />
                    <SortableHeader
                        label="Target Time"
                        field="target_publish_time"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        align="center"
                    />
                    <SortableHeader
                        label="Audience"
                        field="audience"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        align="center"
                    />
                    <SortableHeader
                        label="Type"
                        field="type"
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                        align="center"
                    />
                    <TableCell align="center">
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#7a9cff",
                                fontWeight: 700,
                                textTransform: "uppercase",
                            }}
                        >
                            Sent / Read
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#7a9cff",
                                fontWeight: 700,
                                textTransform: "uppercase",
                            }}
                        >
                            Actions
                        </Typography>
                    </TableCell>
                </HeaderRow>

                {isLoading && (
                    <Box sx={{ p: 6, textAlign: "center" }}>
                        <Typography
                            variant="body2"
                            sx={{ color: "rgba(255,255,255,0.5)" }}
                        >
                            Loading messages…
                        </Typography>
                    </Box>
                )}

                {!isLoading && listError != null && (
                    <Box sx={{ p: 6, textAlign: "center" }}>
                        <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                            {listError?.response?.data?.message ??
                                TEXT_MSGS.NETWORK_ERROR_MSG}
                        </Typography>
                    </Box>
                )}

                {isEmpty && (
                    <Box sx={{ p: 6, textAlign: "center" }}>
                        <Typography
                            variant="body2"
                            sx={{ color: "rgba(255,255,255,0.5)" }}
                        >
                            No marketing messages yet.
                        </Typography>
                    </Box>
                )}

                {!isLoading &&
                    listError == null &&
                    sortedMessages.map((item) => {
                            const currentStatus = (item.status || "").toLowerCase();
                            const isDelivered = currentStatus === "delivered";
                            const isCancelled =
                                currentStatus === "cancel" ||
                                currentStatus === "cancelled";
                            const isInProcess = item.in_process === true;
                            const targetTimePassed = isPublishTimePast(
                                item.target_publish_time,
                            );
                            const isDraft = currentStatus === "draft";
                            const isEditDisabled =
                                isDelivered ||
                                isInProcess ||
                                isCancelled ||
                                targetTimePassed;
                            const isCancelDisabled =
                                isDraft ||
                                isDelivered ||
                                isCancelled ||
                                isInProcess ||
                                targetTimePassed;
                            const editDisabledReason = targetTimePassed
                                ? "Target publish time has passed"
                                : "Edit unavailable";
                            const cancelDisabledReason = targetTimePassed
                                ? "Target publish time has passed"
                                : "Cancel unavailable";
                            const isDeleteDisabled = !isDraft;
                            const deleteDisabledReason =
                                "Only draft messages can be deleted";
                        return (
                                <Row key={item.id}>
                                    <TableCell align="center">
                                        <Tooltip title={item.id} arrow placement="top">
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                sx={{
                                                    color: "#b8c9e8",
                                                    fontFamily:
                                                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                                                    fontSize: "0.8125rem",
                                                    lineHeight: 1.5,
                                                    display: "block",
                                                    width: "100%",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.id}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="start">
                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                width: "100%",
                                                py: 0.25,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.75,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: "#fff",
                                                    lineHeight: 1.45,
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {item.popup_data_obj?.title ||
                                                    "(untitled)"}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                        "rgba(255,255,255,0.5)",
                                                    lineHeight: 1.5,
                                                    display: "block",
                                                    wordBreak: "break-word",
                                                }}
                                            >
                                                {plainTextPreview(
                                                    item.popup_data_obj?.msg,
                                                    80,
                                                )}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <StatusChip status={item.status} processing={item.in_process} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#d1dcf0",
                                                lineHeight: 1.5,
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            {formatTimeMs(
                                                parseCreatedTimeMs(
                                                    item.created_time,
                                                ),
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#d1dcf0",
                                                lineHeight: 1.5,
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            {formatTimeMs(
                                                parseTargetPublishTimeMs(
                                                    item.target_publish_time,
                                                ),
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#d1dcf0",
                                                lineHeight: 1.5,
                                                fontSize: "0.875rem",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {item.user_access_key_list?.length
                                                ? item.user_access_key_list.join(", ")
                                                : "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#d1dcf0",
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {item.popup_data_obj?.type || "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#d1dcf0",
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {item.n_sent || "—"}&nbsp;&nbsp;/&nbsp;&nbsp;{item.n_read || "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 0.5,
                                                justifyContent: "center",
                                                flexWrap: "wrap",
                                                py: 0.5,
                                            }}
                                        >
                                            <Tooltip title="Preview message" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Preview message"
                                                        onClick={() => handlePreview(item)}
                                                        sx={{ color: "#7a9cff" }}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    isEditDisabled
                                                        ? editDisabledReason
                                                        : "Edit message"
                                                }
                                                arrow
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Edit message"
                                                        disabled={isEditDisabled}
                                                        onClick={() =>
                                                            navigate(
                                                                `/marketing-messages/${item.id}`,
                                                                {
                                                                    state: { message: item },
                                                                },
                                                            )
                                                        }
                                                        sx={{
                                                            color: isEditDisabled
                                                                ? "rgba(255,255,255,0.25)"
                                                                : "#7a9cff",
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    isCancelDisabled
                                                        ? cancelDisabledReason
                                                        : "Cancel message"
                                                }
                                                arrow
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Cancel message"
                                                        disabled={isCancelDisabled}
                                                        onClick={() => handleCancel(item)}
                                                        sx={{
                                                            color: isCancelDisabled
                                                                ? "rgba(255,255,255,0.25)"
                                                                : "#ff9800",
                                                        }}
                                                    >
                                                        <CancelIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    isDeleteDisabled
                                                        ? deleteDisabledReason
                                                        : "Delete message"
                                                }
                                                arrow
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Delete message"
                                                        disabled={isDeleteDisabled}
                                                        onClick={() => handleDelete(item)}
                                                        sx={{
                                                            color: isDeleteDisabled
                                                                ? "rgba(255,255,255,0.25)"
                                                                : "#ff6b6b",
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </Row>
                        );
                    })}
            </ListCard>

                {confirmDialog.open && (
                    <ConfirmActionDialog
                        open={confirmDialog.open}
                        title={
                            confirmDialog.type === "delete"
                                ? "Delete marketing message"
                                : "Cancel marketing message"
                        }
                        description={
                            confirmDialog.type === "delete"
                                ? `Delete \"${confirmDialog.item?.popup_data_obj?.title || "this message"}\"? This cannot be undone.`
                                : `Cancel \"${confirmDialog.item?.popup_data_obj?.title || "this message"}\"?`
                        }
                        cancelHandler={() =>
                            setConfirmDialog({
                                open: false,
                                type: null,
                                item: null,
                            })
                        }
                        saveHandler={handleConfirmAction}
                    />
                )}

            {/* Preview modal — reuses the same PopupRenderer/InfoPopup the
               end user actually sees, so what admins preview here is exactly
               what ships. Closing just clears the local state; it never
               touches the backend or the list. */}
            <PopupRenderer
                open={previewData != null}
                onClose={() => setPreviewData(null)}
                popupData={previewData}
            />
        </RootContainer>
    );
}
